import { useState, useEffect } from 'react';
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  User,
  OAuthProvider,
  signInWithCredential
} from 'firebase/auth';
import { auth } from '../../../config/firebase';

export interface AuthUser {
  uid: string;
  email: string | null;
  name: string | null;
  photoURL: string | null;
}

export function useFirebaseAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Format the Firebase user object into our AuthUser type
  const formatUser = (user: User): AuthUser => ({
    uid: user.uid,
    email: user.email,
    name: user.displayName,
    photoURL: user.photoURL
  });

  // Handle user state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setLoading(true);
      if (user) {
        setUser(formatUser(user));
      } else {
        setUser(null);
      }
      setError(null);
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
        throw new Error('Google Client ID is not configured');
      }

      // Create Google OAuth provider
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      // Set custom parameters for Google OAuth
      provider.setCustomParameters({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);
      
      // Get the Google OAuth access token
      const credential = GoogleAuthProvider.credentialFromResult(result);
      
      if (credential) {
        // Sign in with the credential
        await signInWithCredential(auth, credential);
        setUser(formatUser(result.user));
      } else {
        throw new Error('Failed to get Google credential');
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError(error instanceof Error ? error.message : 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      setUser(null);
      setError(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setError(error instanceof Error ? error.message : 'Failed to sign out');
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    signInWithGoogle,
    signOut: signOutUser
  };
} 